// client/src/components/pages/PeriodSummary/DailyChartTooltip.js
//
// The one tooltip body used by everything on the Period Summary page that plots
// a day: the Timeline's overall chart, the Timeline's lane hover, and the Day
// tab of the group detail panel. Same three lines everywhere (dispatch, receipt,
// difference) so the reader learns it once.
//
// Token-backed classes, not `contentStyle` hexes — recharts only needs JS colour
// values for SVG attributes, and a DOM tooltip is plain HTML that can re-theme
// on its own.

import React from 'react';
import { DISPATCH_COLOR, RECEIPT_COLOR, formatTonnes } from './utils';

export const TooltipRow = ({ color, label, value }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="flex items-center gap-1.5 text-gray-500">
      {color && (
        <span
          className="inline-block w-2 h-2 rounded-sm"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      )}
      {label}
    </span>
    <span className="font-mono tabular-nums">{value}</span>
  </div>
);

/** Dispatch / receipt / difference for one day. */
export const DayTooltipBody = ({ title, subtitle, dispatchTonnes, receiptTonnes, suffix }) => {
  const diff = (Number(receiptTonnes) || 0) - (Number(dispatchTonnes) || 0);
  return (
    <>
      {title && (
        <div className="font-semibold mb-1 truncate" title={title}>
          {title}
        </div>
      )}
      {subtitle && <div className="text-gray-500 mb-1">{subtitle}</div>}
      <TooltipRow
        color={DISPATCH_COLOR}
        label={`Dispatch${suffix?.dispatch || ''}`}
        value={`${formatTonnes(dispatchTonnes)} t`}
      />
      <TooltipRow
        color={RECEIPT_COLOR}
        label={`Receipt${suffix?.receipt || ''}`}
        value={`${formatTonnes(receiptTonnes)} t`}
      />
      <div className="mt-1 pt-1 border-t border-gray-100">
        <TooltipRow label="Difference" value={`${diff > 0 ? '+' : ''}${formatTonnes(diff)} t`} />
      </div>
    </>
  );
};

/** recharts `content` renderer wrapping DayTooltipBody in a themed card. */
export const RechartsDayTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className="rounded-md border border-gray-200 bg-surface-raised shadow-lg px-2.5 py-2 text-[11px] text-slate-800 min-w-[11rem]">
      <DayTooltipBody
        subtitle={label}
        dispatchTonnes={point.dispatchTonnes}
        receiptTonnes={point.receiptTonnes}
      />
    </div>
  );
};

export default RechartsDayTooltip;
